import React, {Fragment, useState} from 'react'
import {useParams, Link} from 'react-router-dom'
import {pluralise, titlecase} from 'admin/lib/format'
import useEffect from 'admin/hooks/useEffect'
import useDocument from 'admin/hooks/useDocument'
import useBoolState from 'admin/hooks/useBoolState'
import selectableCollection from 'admin/components/hoc/selectableCollection'
import Pagination from 'admin/components/shared/Pagination'
import Dropdown from 'admin/components/shared/Dropdown'
import InputField from 'admin/components/shared/InputField'
import usePaymentRules from 'admin/hooks/usePaymentRules'
import Page from 'admin/components/shared/Page'
import sortable from 'admin/components/hoc/sortable'
import Flash from '../shared/Flash/index'
import Deletion from '../Deletion'
import ModalLink from '../shared/ModalLink'
import useModal from '../../hooks/useModal'

const Table = ({onClick, onSort, sortBy, sortDirection, data, SelectAll, SelectOne}) => {
  const {organisationID} = useParams()
  const SortableTH = sortable('th', {onSort, sortBy, sortDirection, camelcase: true})

  return (
    <table className='table is-striped is-hoverable is-fullwidth is-linked'>
      <colgroup>
        <col span='1' style={{width: '4.5%'}} />
        <col span='1' style={{width: '25.5%'}} />
        <col span='1' style={{width: '70%'}} />
      </colgroup>

      <thead>
        <tr>
          <th>
            <SelectAll />
          </th>
          <th>Name</th>
          <th>Conditions</th>
        </tr>
      </thead>

      <tbody>
        {data.map((rule, i) => (
          <tr
            key={i}
            onClick={e => {
              if (e.target.tagName.toLowerCase() !== 'a' && e.target.type !== 'checkbox') {
                e.preventDefault()
                onClick(rule.id)
              }
            }}>
            <td>
              <SelectOne identifier={rule.id} />
            </td>
            <td>
              <Link to={`/orgs/${organisationID}/accounts/${rule.account.id}`}>{rule.account.displayName}</Link>
              {rule.account.displayName !== rule.account.contactFullName && !!rule.account.contactFullName ? (
                <sub className='has-text-weight-semibold'>{rule.account.contactFullName}</sub>
              ) : null}
            </td>
            <td>
              {rule.conditions.map((condition, i) => (
                <Fragment key={i}>
                  {titlecase(condition.fieldName)} {condition.matchType}{' '}
                  <span className='has-text-weight-bold'>{condition.text}</span>
                  {i < rule.conditions.length - 1 ? ' | ' : null}
                </Fragment>
              ))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export default () => {
  const {organisationID} = useParams()
  const {pushModal} = useModal()
  const {paymentRules: rules, totalCount, filters, setFilters, destroy} = usePaymentRules(organisationID)

  const [selected, setSelected] = useState([])
  const [showDeleteModal, setShowDeleteModal, toggleShowDeleteModal] = useBoolState()

  const title = `Payment rules`

  useDocument({title})

  useEffect(() => setSelected([]), [rules])

  const SelectableTable = selectableCollection(Table, {
    onSelect: setSelected,
    selectedIdentifiers: selected
  })

  return (
    <Fragment>
      <Flash />

      {showDeleteModal && (
        <Deletion onClose={toggleShowDeleteModal} onConfirm={({notes}) => destroy({recordIds: selected, notes})} />
      )}

      <Page
        title={title}
        subtitle={
          totalCount
            ? `Showing ${rules.length} of ${pluralise(`payment rule`, totalCount, true)}`
            : 'No payment rules found'
        }
        buttons={
          <ModalLink modal='new-payment-rule' className='button is-light'>
            New payment rule
          </ModalLink>
        }>
        {rules.size != 0 ? (
          <form className='box is-radiusless is-shadowless has-background-filters'>
            <div className='field is-grouped'>
              <div className='control is-expanded'>
                <InputField
                  type='search'
                  name='q'
                  label='Search'
                  autoComplete='off'
                  value={filters.q}
                  debounce={true}
                  onChange={changes => setFilters({...changes, page: 1})}
                />
              </div>
            </div>
          </form>
        ) : null}

        {rules.length != 0 ? (
          <Fragment>
            <div className='level'>
              {selected.length ? (
                <div className='level-left has-background-info box mb-0 is-shadowless p-3 pl-4'>
                  <div className='level-item has-text-white'>
                    {selected.length === rules.length ? 'All ' : ''}
                    {pluralise('payment rule', selected.length, true)} on this page {pluralise('is', selected.length)}{' '}
                    selected
                  </div>
                  <Dropdown id='rules-actions-menu' className='level-item'>
                    <button className='button is-outlined is-small'>
                      <span>Action</span>
                      <span className='icon is-small'>
                        <i className='fa fa-caret-down' aria-hidden='true'></i>
                      </span>
                    </button>
                    <a onClick={toggleShowDeleteModal} className='dropdown-item'>
                      Delete
                    </a>
                  </Dropdown>
                </div>
              ) : (
                <div className='level-left'></div>
              )}

              <div className='level-right'>
                <Pagination
                  page={filters.page}
                  limit={filters.limit}
                  total={totalCount}
                  onChange={setFilters}
                  className='level-item'
                />
              </div>
            </div>

            <SelectableTable
              data={rules}
              onClick={paymentRuleId => pushModal('payment-rule', {paymentRuleId})}
              onSort={setFilters}
              sortBy={filters.sortBy}
              sortDirection={filters.sortDirection}
            />

            {rules.length >= 10 && (
              <div className='level'>
                <div className='level-left'></div>
                <div className='level-right'>
                  <Pagination
                    page={filters.page}
                    limit={filters.limit}
                    total={totalCount}
                    onChange={setFilters}
                    className='level-item'
                  />
                </div>
              </div>
            )}
          </Fragment>
        ) : null}
      </Page>
    </Fragment>
  )
}
